import "./styles.scss";

import { useRef, useState } from "react";
import CustomModal from "@components/CustomModal";
import { Input, Text, Icon } from "@fragments";
import { IconMailForward, IconArrowFork } from "@tabler/icons-react";

import api from "@helpers/api";

const INITIAL = { email: "", percentile: "", notes: "" };

/**
 * Send split invitation (§3) — replaces the UsersModal add-flow. Imperative open
 * like the Mentoring InviteModal:
 *   modalRef.current.open(communityId, onSent)
 * Collects e-mail (required) + target percentile (1–99, must leave room to
 * renormalize the existing receivers) + an optional message. Posts to
 * community/{id}/split/invitations; 4xx maps to field errors + a toast.
 */
export default function InviteReceiverModal({ modalRef }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  // Max % a new receiver can be promised — the total minus the fixed mentoring
  // shares and what other open invites already claim (existing coproducers
  // renormalize down, so they don't reduce it). Whole number for the integer input.
  const [available, setAvailable] = useState(100);

  const communityIdRef = useRef(null);
  const onSentRef = useRef(null);

  modalRef.current = {
    open(communityId, availableRoom, onSent) {
      communityIdRef.current = communityId ?? null;
      onSentRef.current = onSent;
      setAvailable(Number.isFinite(availableRoom) ? availableRoom : 100);
      setForm(INITIAL);
      setErrors({});
      setOpen(true);
    },
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // The percentile is an ABSOLUTE share of the total; it must fit the room still
  // available (mirrors the backend SplitPercentileWithinAvailableRule).
  const maxAllowed = Math.floor(available);

  function validate() {
    const next = {};
    const email = form.email.trim();
    if (!email) next.email = "Informe o e-mail do convidado.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      next.email = "Informe um e-mail válido.";

    const pct = Number(form.percentile);
    if (form.percentile === "" || Number.isNaN(pct))
      next.percentile = "Informe o percentual de repasse.";
    else if (pct <= 0) next.percentile = "O percentual deve ser maior que 0.";
    else if (pct > available)
      next.percentile = `O percentual excede o disponível (${maxAllowed}%).`;

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;

    const communityId = communityIdRef.current;
    if (!communityId) return;

    setLoading(true);

    const payload = {
      email: form.email.trim(),
      percentile: Number(form.percentile),
      notes: form.notes || null,
    };

    const res = await api.post(`community/${communityId}/split/invitations`, payload);

    setLoading(false);

    if (res?.response?.status >= 400) {
      setErrors(mapErrors(res.response.data?.errors));
      window.setSnackbar?.(
        res.response.data?.message || "Não foi possível enviar o convite",
      );
      return;
    }

    window.setSnackbar?.("Convite enviado");
    onSentRef.current?.(res.data);
    setOpen(false);
  }

  return (
    <CustomModal
      maxWidth={520}
      open={open}
      handleClose={() => setOpen(false)}
      title="Convidar recebedor"
      bottomButtonSumbitText="Enviar convite"
      bottomButtonSumbitIcon={<IconMailForward size={18} />}
      handleSubmit={handleSubmit}
      loading={loading}
      bodyComponent={
        open ? (
          <div className="invite-receiver-modal">
            <div className="irm-head">
              <Icon.Sphere size={56}>
                <IconArrowFork />
              </Icon.Sphere>
              <div className="irm-head-text">
                <Text.h5 contrast={9} weight={500}>
                  Convide alguém para receber parte da receita
                </Text.h5>
                <Text.span contrast={5} maxLines={2}>
                  O convidado recebe um e-mail e confirma na própria conta. A cota
                  só entra no split depois do aceite, renormalizando as demais.
                </Text.span>
              </div>
            </div>

            <Input.Email
              label="E-mail do convidado"
              value={form.email}
              onInput={(v) => set("email", v)}
              error={!!errors.email}
              helper={errors.email}
              autoFocus
            />
            <Input.Integer
              label="Percentual de repasse"
              value={form.percentile}
              onInput={(v) => set("percentile", v)}
              max={2}
              end="%"
              error={!!errors.percentile}
              helper={errors.percentile || `Disponível para repasse: ${maxAllowed}%`}
              placeholder="Ex.: 10"
            />
            <Input.Text
              label="Mensagem (opcional)"
              value={form.notes}
              onInput={(v) => set("notes", v)}
              multiline
              placeholder="Inclua um recado pessoal no convite"
            />
          </div>
        ) : null
      }
    />
  );
}

function mapErrors(fieldErrors = {}) {
  return Object.entries(fieldErrors).reduce((acc, [key, messages]) => {
    acc[key] = Array.isArray(messages) ? messages[0] : String(messages);
    return acc;
  }, {});
}
